import { useState, useEffect } from "react";
import { Box, FormControl, styled, Autocomplete, TextField, Checkbox } from "@mui/material";
import FormControlLabel from '@mui/material/FormControlLabel';
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Site from "../../../api/Admin/Site";
import EmailAddressTextField from "../../../components/EmailAddressField";
import BlendFacility from "../../../api/Formulations/BlendFacility";
import AuditInfoLabel from "../../../components/AuditInfoLabel";
import ModalSimpleButton from '../../../components/Modal/ModalSimpleButton';
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import { useAuth } from "../../../context/AuthContext";
import { Roles } from "../../../constants/roles";

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "25px"
}));

const blendFacilityValidationSchema = (invalidBlendFacilityNames) => {
  return Yup.object().shape({
    blendFacilityName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Blend Facility Name is required')
      .max(50, 'Must be 50 characters or less')
      .test("unique-name", "Blend Facility Name already exists", (value) => {
        if (!value) return true;
        const lowerCaseValue = value.toLowerCase().trim();
        return !invalidBlendFacilityNames.some((name) => name?.toLowerCase().trim() === lowerCaseValue);
      }),
     preparationThresholdPercent: Yup.number()
      .typeError('Must be a number')
      .min(0, 'Minimum is 0.00')
      .max(100, 'Maximum is 100.00')
      .required('Threshold Percent is required'), 
     siteName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Site Name is required')
  });
}

const initialState = {
  blendFacilityName: "",
  isActive: false,
  isExternalBlendFacility: false,
  primaryContactEmail: "",
  preparationThresholdPercent: (1).toFixed(2),
  siteName: ""
};

const BlendFacilityManagement = () => {
  const [isNewBlendFacility, setIsNewBlendFacility] = useState(false);
  const [blendFacilityData, setBlendFacilityData] = useState(initialState);
  const [blendFacilityList, setBlendFacilityList] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [primaryContactEmailError, setPrimaryContactEmailError] = useState(false);

  const [showInactiveFacilities, setShowInactiveFacilities] = useState(false);   

  const [modalSimpleButton, setModalSimpleButton] = useState(false);
  const [modalSimpleButtonText, setModalSimpleButtonText] = useState('');
  const [modalSimpleTitle, setModalSimpleTitle] = useState('');
  const [modalSimpleText, setModalSimpleText] = useState('');

  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.BlendSME, Roles.Developer]);

  const initialValues = blendFacilityData;
  const invalidBlendFacilityNames = blendFacilityList.filter(s => s.blendFacilityName !== blendFacilityData.blendFacilityName).map(s => s.blendFacilityName);

  const filteredBlendFacilities = blendFacilityList?.filter(f => showInactiveFacilities || f.isActive) ?? [];

  const resetData = () => setBlendFacilityData(initialState);

  useEffect(() => {
    loadAllBlendFacilities();
    loadAllSites();
  }, []);

  function loadAllBlendFacilities() {
    BlendFacility.getAllFacilities().then((res) => {
      if (res.length > 0) {
        let array = [];
        array.push({ blendFacilityName: "" });
        array.push(
          ...res.map(x => ({
            ...x,
            preparationThresholdPercent: !isNaN(x.preparationThresholdPercent) && x.preparationThresholdPercent !== '' 
              ? parseFloat(x.preparationThresholdPercent).toFixed(2) : ''
          }))
        );

        setBlendFacilityList(array.sort(
          (a,b) => a.blendFacilityName.localeCompare(b.blendFacilityName)
        ));
      }
    }); 
  }

  function loadAllSites() {
    Site.getAll().then((res) => {
      if (res.length > 0) {

        let array = [];
        array.push({ siteName: "", abbreviation: "" });
        array.push(...res);

        setSiteList(array.sort(
          (a,b) => a.siteName.localeCompare(b.siteName)
        ));
      }
    }); 
  }

  function handleBlendFacilitySelection(blendFacility) {
    resetData();
    
    if (blendFacility?.blendFacilityName) {
      setIsNewBlendFacility(false);
      setBlendFacilityData(blendFacility);
    }
  }

  function handleAddNewSite() {
    setIsNewBlendFacility(true);
    resetData();
  }

  const handleSubmit = async(values) => {
    if (primaryContactEmailError) return;
    
    setIsSaving(true);

    const updatedBlendFacilityData = new BlendFacility({
      blendFacilityName: values.blendFacilityName,
      isActive: values.isActive,
      isExternalBlendFacility: values.isExternalBlendFacility,
      primaryContactEmail: values.primaryContactEmail,
      preparationThresholdPercent: values.preparationThresholdPercent,
      siteName: values.siteName
    });

    try {
      const apiAction = isNewBlendFacility
        ? BlendFacility.add(updatedBlendFacilityData)
        : BlendFacility.update(blendFacilityData.blendFacilityName, updatedBlendFacilityData);

      await apiAction;

      resetData();
      openModalSimpleButton('Success', 'Blend Facility saved successfully!', 'Ok');
      setIsNewBlendFacility(false);
      loadAllBlendFacilities();

    } catch (error) {
      openModalSimpleButton('Fail', `Error saving Blend Facility : ${error?.message}`, 'Ok');      
    } finally {
      setIsSaving(false);
    } 
  }

  function closeModalSimpleButton() {
    setModalSimpleButton(false);
  }

  function openModalSimpleButton(title, text, buttonText) {
    setModalSimpleButtonText(buttonText)
    setModalSimpleButton(true);
    setModalSimpleTitle(title);
    setModalSimpleText(text);
  }

  function toggleShowInactiveFacilities() {
    const showInactive = !showInactiveFacilities;
    setShowInactiveFacilities(showInactive);

    if (!isNewBlendFacility && !showInactive && blendFacilityData?.isActive === false) {
      resetData();
    }
  }

  return (
    <StyledBox flexDirection="column" justifyContent="space-between">

      <FormControlLabel sx={{ marginLeft: "5px", marginBottom: "5px"}}
        control={
          <Checkbox
            checked = {showInactiveFacilities}
            onChange={() => toggleShowInactiveFacilities()}
          />}
        label="Show inactive Blend Facilities"         
      />

      <StyledBox>
        <FormControl sx={{ m: 1, width: "44rem", display: "flex" }} variant='outlined'>
          <StyledBox sx={{ justifyContent: "space-between" }}>
            <Autocomplete
              noOptionsText={"Loading Blend Facilities..."}
              id="facility-select"
              value={blendFacilityData}
              onChange={(e, value) => { handleBlendFacilitySelection(value) }}
              disablePortal
              options={filteredBlendFacilities ?? []}
              getOptionLabel={(option) => option?.blendFacilityName ?? ""}
              sx={{ width: "35rem" }}
              renderInput={(params) => <TextField {...params} variant="outlined" size="small" margin="none" label="Blend Facility"/> }
              autoSelect
            />

            { canEdit && <GlobalSecondaryButton sx={{width:"8rem", marginLeft:"1rem" }}
                variant='contained'
                type="button"
                onClick={handleAddNewSite}
              >Add New</GlobalSecondaryButton>
            }

          </StyledBox>
        </FormControl>
      </StyledBox>

      {(blendFacilityData.blendFacilityName || isNewBlendFacility) && <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={() => blendFacilityValidationSchema(invalidBlendFacilityNames)}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, handleBlur, setFieldValue, setFieldTouched, errors, touched, isValid, dirty }) => (
            <Form style={{margin:"10px", width: "40rem"}}>

              <StyledBox>

                <TextField
                  style={{width: "35rem"}}
                  name="blendFacilityName"
                  label="Blend Facility name"
                  variant="outlined" size="small" margin="none" 
                  value={values.blendFacilityName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!isNewBlendFacility}
                  error={touched.blendFacilityName && Boolean(errors.blendFacilityName)}
                  helperText={touched.blendFacilityName && errors.blendFacilityName}
                />
    
              </StyledBox>
    
              <StyledBox>

                <TextField style={{ width: "15rem" }}
                  name="preparationThresholdPercent"
                  label="Preparation Threshold Percent"
                  variant="outlined" size="small" margin="none"
                  value={values.preparationThresholdPercent} 
                  onChange={handleChange}
                  onBlur={handleBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  disabled={!canEdit}
                  error={touched.preparationThresholdPercent && Boolean(errors.preparationThresholdPercent)}
                  helperText={touched.preparationThresholdPercent && errors.preparationThresholdPercent}
                />
    
                <Box style={{ justifyContent: "space-between" }}>
                  <FormControlLabel
                    label="Active"
                    style={{marginLeft:"40px"}}
                    control={
                      <Checkbox
                        onChange={handleChange}
                        name="isActive"
                        checked={values.isActive}
                        disabled={!canEdit} 
                      />}
                  />
    
                  <FormControlLabel
                    label="External Facility"
                    style={{marginLeft:"30px"}}
                    control={
                      <Checkbox
                        onChange={handleChange}
                        name="isExternalBlendFacility"
                        checked={values.isExternalBlendFacility}
                        disabled={!canEdit}  
                      />}
                  />
                </Box>
                
              </StyledBox> 

              <StyledBox>

                <EmailAddressTextField
                  key='primaryContactEmail'
                  validatedUserEmail={values.primaryContactEmail}
                  setValidatedUserEmail={(value) => {
                    setFieldValue('primaryContactEmail', value);
                  }}
                  setHasErrors={setPrimaryContactEmailError}
                  hasErrors={primaryContactEmailError}
                  isDisabled={!canEdit}
                  isRequired={false}
                  labelText="Primary Contact Email"
                  placeholderText="Primary Contact Email"
                  size={"small"}
                  margin={"none"}
                  fontSize={16}
                  fieldWidth={"78%"}
                  showPlusMeButton={true}
                />

              </StyledBox>
    
              <StyledBox style={{marginBottom: "30px"}}>

                <Autocomplete
                  noOptionsText={"Loading Sites..."}
                  value={siteList.find(p => p.siteName === values.siteName) || null}
                  onChange={(e, val) => setFieldValue('siteName', val?.siteName ?? '')}
                  isOptionEqualToValue={(option, value) => option.siteName === value.siteName}
                  getOptionLabel={(s) => s.isExternalSite ? `EXT - ${s.siteName}` : s.siteName}
                  onBlur={() => setFieldTouched('siteName', true)}
                  disablePortal
                  disabled={!canEdit || siteList.length === 0}
                  options={siteList}
                  sx={{ width: "35rem" }}
                  renderInput={(params) => <TextField 
                    {...params} 
                    name="siteName"  variant="outlined" size="small" margin="none" 
                    label="Site Name"
                    error={touched.siteName && Boolean(errors.siteName)}
                    helperText={touched.siteName && errors.siteName}
                  />}
                  autoSelect                  
                />
    
              </StyledBox>

              <AuditInfoLabel auditObject={values} />            

              { canEdit &&
                <StyledBox style={{ marginTop: "30px", marginBottom: "20px" }}>
                  <GlobalButton sx={{ width: "8rem" }}
                    variant='contained'             
                    type="submit"
                    disabled={!dirty || isSaving }
                  >
                    Save
                  </GlobalButton>
                </StyledBox>
              }
    
            </Form>
          )}
        </Formik>
      } 

      <ModalSimpleButton 
        title={modalSimpleTitle} 
        buttonText={modalSimpleButtonText} 
        buttonAction={closeModalSimpleButton} 
        open={modalSimpleButton} 
        setOpen={setModalSimpleButton}
      >
        <label>
          {modalSimpleText}
        </label>
      </ModalSimpleButton>

    </StyledBox>
  );
};

export default BlendFacilityManagement;
